import { Close } from '@tunasong/icons'
import { Button, IconButton } from '@tunasong/ui-lib'
import type { FC, HTMLAttributes, ReactNode } from 'react'
import { PartLineText } from './part/part-line-text.js'
import styles from './performance-header.module.css'

export interface PerformanceHeaderProps extends HTMLAttributes<HTMLElement> {
  /** "Settliste 2026 · Song 2 of 19", or "Playlist · Track 7 of 9". Absent outside a set. */
  eyebrow?: string
  /** The item in view: the song, the track or the set itself on its overview */
  title?: string
  /** Leave performance mode */
  onExit: () => void
  /** What the performer's parts need ("Key A · ♩=86 · 4/4 · count-in 1 bar"), as text */
  partLine?: string
  /** "Playing Vocals + Guitar ▾": the performer's part menu. */
  partMenu?: ReactNode
  /** "Shapes | Concert": present only with a capo and Guitar among the parts. */
  pitchFrame?: ReactNode
  /** "My notes (n)" toggle. */
  notesToggle?: ReactNode
  /** The Display popover (text size, notes placement, columns, flash). */
  display?: ReactNode
  /** The scroll progress, drawn on the header's bottom edge */
  progress?: ReactNode
  /**
   * Phones: a row of Exit (×), the title, My notes and Display, over a row with the part line and the part menu.
   * Pass the compact My notes and Display with it.
   */
  compact?: boolean
}

/**
 * The performance header: one solid row at every desktop width. Exit, where you are in the set, the part line as
 * text (it truncates rather than pushing the controls onto a second row), then the controls on the right.
 * Phones give the part line a row of its own.
 */
export const PerformanceHeader: FC<PerformanceHeaderProps> = props => {
  const {
    eyebrow,
    title,
    onExit,
    partLine,
    partMenu,
    pitchFrame,
    notesToggle,
    display,
    progress,
    compact = false,
    className,
    ...headerProps
  } = props

  const titleBlock = (
    <div className={styles.titleBlock}>
      {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
      {title ? <div className={styles.title}>{title}</div> : null}
    </div>
  )
  const partLineText = <div className={styles.partLine}>{partLine ? <PartLineText line={partLine} /> : null}</div>

  if (compact) {
    return (
      <header {...headerProps} className={[styles.headerWrapper, styles.compact, className].filter(Boolean).join(' ')}>
        <div className={styles.header} data-row="main">
          <IconButton className={styles.exit} aria-label="Exit performance mode" onClick={onExit}>
            <Close />
          </IconButton>
          {titleBlock}
          <div className={styles.rightSection}>
            {notesToggle}
            {display}
          </div>
        </div>
        {partLine || partMenu || pitchFrame ? (
          <div className={styles.partRow} data-row="part">
            {partLineText}
            {partMenu}
            {pitchFrame}
          </div>
        ) : null}
        <div id="performance-structure-map" className={styles.structureMapSlot} />
        {progress}
      </header>
    )
  }

  return (
    <header {...headerProps} className={[styles.headerWrapper, className].filter(Boolean).join(' ')}>
      <div className={styles.header} data-row="main">
        <Button
          variant="outlined"
          color="inherit"
          className={styles.exit}
          startIcon={<Close />}
          aria-label="Exit performance mode"
          onClick={onExit}
        >
          Exit
        </Button>
        {titleBlock}
        {partLineText}
        <div className={styles.rightSection}>
          {partMenu}
          {pitchFrame}
          {notesToggle}
          {display}
        </div>
      </div>
      <div id="performance-structure-map" className={styles.structureMapSlot} />
      {progress}
    </header>
  )
}

export default PerformanceHeader
